import React from 'react';
import { Icon, Header, Loader, Table, Label, Segment, Button } from 'semantic-ui-react';
import moment from 'moment';
import FileSaver from 'file-saver';
import * as ExcelJS from 'exceljs/dist/exceljs.js';
import { reportView } from '../reports/helper/dataService';
import { getActiveAssetList } from '../reports/helper/helper';
import reportsEnv from '../reports/reportsEnv';
import { alertMessage } from '../../helper/alert-message';
import { inject, observer } from 'mobx-react';

const INACTIVE_DAYS = 3;

@inject('assets')
@observer
class NeedToCheck extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      data: [],
      excelDisableBtn: false,
    };
  }

  componentDidMount() {
    const activeAssets = getActiveAssetList(this.props.assets.data).map(
      (item) => item.value,
    );

    if (activeAssets.length > 0) {
      this.setState({ loading: true });
      reportView(reportsEnv.locationLive, {
        asset_ids: activeAssets,
      })
        .then((response) => {
          const cutoff = moment().subtract(INACTIVE_DAYS, 'days');
          const inactiveData = response.data
            .filter(({ last_data }) =>
              moment.unix(last_data.time).isBefore(cutoff),
            )
            .map(({ last_data }) => last_data);

          this.setState({ data: inactiveData, loading: false });
        })
        .catch(() => {
          this.setState({ loading: false });
          alertMessage('Sorry! Something went wrong');
        });
    }
  }

  handleDownloadExcel = () => {
    this.setState({ excelDisableBtn: true });

    const { data } = this.state;
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Finder GPS Tracking';
    workbook.created = new Date();

    workbook.addWorksheet('Need To Check');
    const worksheet = workbook.getWorksheet('Need To Check');

    worksheet.addRow(['Finder GPS Tracking']);
    worksheet.addRow(['Need To Check']);
    worksheet.addRow([
      `Generated on ${moment().format('MMMM Do, YYYY, hh:mm:ss A')}`,
    ]);
    worksheet.addRow();
    worksheet.addRow([`${data.length} assets`]);
    worksheet.addRow();

    worksheet.columns = [
      { width: 10 },
      { width: 25 },
      { width: 35 },
      { width: 35 },
    ];

    const headerRow = worksheet.addRow(['ID', 'Vehicle Name', 'Last Location Time', 'Nearest Landmark']);
    headerRow.eachCell((cell) => {
      cell.font = {
        bold: true,
        color: { argb: 'FF000000' },
      };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'CCCCCC' },
      };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    data.forEach(({ name, time, landmark }, idx) =>
      worksheet.addRow([
        idx + 1,
        name,
        moment.unix(time).format('dddd, MMMM Do, YYYY, hh:mm:ss A'),
        landmark && landmark.length > 0
          ? landmark.map((item, i) => `${i + 1}. ${item}`).join('. ')
          : 'No Landmark Found',
      ]),
    );

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: 'application/octet-binary',
      });
      FileSaver.saveAs(blob, `Need_To_Check_${moment().format('DD-MMMM-YYYY')}.xlsx`);
      this.setState({ excelDisableBtn: false });
    });
  };

  render() {
    const { loading, data, excelDisableBtn } = this.state;

    const isAuthorizedOrg = ['660e2797faf103f4ae9b9c8d'].includes(
      window.localStorage.getItem('organization'),
    );
    const isAdmin = window.localStorage.getItem('org_admin') === 'true';

    if (!isAuthorizedOrg || !isAdmin) {
      return (
        <h2 style={{ textAlign: 'center' }}>
          You are not authorized person.
        </h2>
      );
    }

    return (
      <Segment style={{ minHeight: '100vh' }}>
        <Segment>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Icon name='clock' size='big' style={{ marginRight: 6 }} />
              <span style={{ fontSize: 22, fontWeight: 700, marginRight: 20 }}>Need To Check</span>
              {!loading && (
                <Label size='large' color='black'>
                  {data.length} assets
                </Label>
              )}
            </div>
            {!loading && data.length > 0 && (
              <Button
                color='teal'
                icon='file excel outline'
                content='Download'
                onClick={this.handleDownloadExcel}
                disabled={excelDisableBtn}
                loading={excelDisableBtn}
              />
            )}
          </div>
        </Segment>
        {loading ? (
          <div style={{ padding: '40px 0' }}>
            <Loader active={loading} size='large' inline='centered'>
              Loading assets...
            </Loader>
          </div>
        ) : data.length > 0 ? (
          <Segment>
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>ID</Table.HeaderCell>
                  <Table.HeaderCell>Vehicle Name</Table.HeaderCell>
                  <Table.HeaderCell>Last Location Time</Table.HeaderCell>
                  <Table.HeaderCell>Nearest Landmark</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {data.map(({ name, time, landmark }, idx) => (
                  <Table.Row key={name}>
                    <Table.Cell>
                      <Label>{idx + 1}</Label>
                    </Table.Cell>
                    <Table.Cell>{name}</Table.Cell>
                    <Table.Cell>
                      {moment
                        .unix(time)
                        .format('dddd, MMMM Do, YYYY, hh:mm:ss A')}
                    </Table.Cell>
                    <Table.Cell>
                      {landmark && landmark.length > 0 ? (
                        landmark.map((item, index) => (
                          <p key={item + index}>
                            <i className='marker icon' />
                            {item}
                          </p>
                        ))
                      ) : (
                        <p>
                          <i className='marker icon' />
                          No data found
                        </p>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Segment>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Header as='h3'>
              <Icon name='check circle outline' />
              All assets are active.
            </Header>
          </div>
        )}
      </Segment>
    );
  }
}

export default NeedToCheck;
